import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Skeleton } from '../components/ui/skeleton';
import { 
  FileText,
  Plus,
  Search,
  Download,
  Copy,
  Eye,
  Edit,
  Trash2,
  Star,
  Clock,
  FileSignature,
  Shield,
  Handshake,
  Receipt,
  Briefcase,
  CheckCircle,
  Loader2,
  X,
  MoreVertical
} from 'lucide-react';
import { templates } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { useTranslation } from '../i18n';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";

// Pre-built templates
const TEMPLATE_LIBRARY = [
  {
    id: 'nda-mutual',
    name: 'Mutual NDA',
    i18nName: 'templates.lib.ndaMutual',
    i18nDesc: 'templates.lib.ndaMutualDesc',
    category: 'legal',
    description: 'Standard mutual non-disclosure agreement for protecting confidential information between two parties.',
    icon: Shield,
    popular: true,
    content: `MUTUAL NON-DISCLOSURE AGREEMENT

This Mutual Non-Disclosure Agreement ("Agreement") is entered into as of [DATE] by and between:

Party A: [COMPANY NAME A]
Address: [ADDRESS A]

Party B: [COMPANY NAME B]
Address: [ADDRESS B]

1. PURPOSE
The parties wish to explore a business opportunity of mutual interest and in connection with this opportunity, each party may disclose to the other certain confidential technical and business information which the disclosing party desires the receiving party to treat as confidential.

2. DEFINITION OF CONFIDENTIAL INFORMATION
"Confidential Information" means any information disclosed by either party to the other party, either directly or indirectly, in writing, orally or by inspection of tangible objects, which is designated as "Confidential," "Proprietary" or some similar designation.

3. NON-USE AND NON-DISCLOSURE
Each party agrees not to use any Confidential Information of the other party for any purpose except to evaluate and engage in discussions concerning a potential business relationship between the parties.

4. TERM
This Agreement shall remain in effect for a period of [TERM] years from the date of execution.

5. RETURN OF MATERIALS
All documents and other tangible objects containing or representing Confidential Information shall be and remain the property of the disclosing party.

6. NO OBLIGATION
Nothing in this Agreement shall obligate either party to proceed with any transaction between them.

7. GOVERNING LAW
This Agreement shall be governed by and construed in accordance with the laws of [JURISDICTION].

IN WITNESS WHEREOF, the parties have executed this Agreement as of the date first written above.

[COMPANY NAME A]                    [COMPANY NAME B]
By: _________________              By: _________________
Name:                              Name:
Title:                             Title:
Date:                              Date:`
  },
  {
    id: 'purchase-agreement',
    name: 'Purchase Agreement',
    i18nName: 'templates.lib.purchaseAgreement',
    i18nDesc: 'templates.lib.purchaseAgreementDesc',
    category: 'sales',
    description: 'Standard purchase agreement template for B2B product sales with terms and conditions.',
    icon: Handshake,
    popular: true,
    content: `PURCHASE AGREEMENT

Agreement Date: [DATE]
Agreement Number: [AGREEMENT_NUMBER]

SELLER:
Company: [SELLER_COMPANY]
Address: [SELLER_ADDRESS]
Contact: [SELLER_CONTACT]

BUYER:
Company: [BUYER_COMPANY]
Address: [BUYER_ADDRESS]
Contact: [BUYER_CONTACT]

1. PRODUCTS/SERVICES
| Item | Description | Quantity | Unit Price | Total |
|------|-------------|----------|------------|-------|
| [ITEM_1] | [DESC_1] | [QTY_1] | [PRICE_1] | [TOTAL_1] |

Subtotal: [SUBTOTAL]
Tax ([TAX_RATE]%): [TAX_AMOUNT]
TOTAL: [GRAND_TOTAL]

2. PAYMENT TERMS
- Payment Method: [PAYMENT_METHOD]
- Payment Schedule: [PAYMENT_SCHEDULE]
- Late Payment: [LATE_FEE]% per month on overdue amounts

3. DELIVERY
- Delivery Date: [DELIVERY_DATE]
- Delivery Location: [DELIVERY_ADDRESS]
- Shipping Terms: [INCOTERMS]

4. WARRANTY
Seller warrants that the products shall be free from defects in material and workmanship for a period of [WARRANTY_PERIOD] from delivery date.

5. ACCEPTANCE
Buyer shall have [INSPECTION_PERIOD] days from delivery to inspect and accept or reject the products.

6. LIMITATION OF LIABILITY
In no event shall either party be liable for any indirect, incidental, special, or consequential damages.

7. GOVERNING LAW
This Agreement shall be governed by the laws of [JURISDICTION].

SIGNATURES:

SELLER:                              BUYER:
_____________________               _____________________
Name:                               Name:
Title:                              Title:
Date:                               Date:`
  },
  {
    id: 'service-agreement',
    name: 'Service Agreement',
    i18nName: 'templates.lib.serviceAgreement',
    i18nDesc: 'templates.lib.serviceAgreementDesc',
    category: 'services',
    description: 'Professional services agreement template with scope of work and deliverables.',
    icon: Briefcase,
    popular: false,
    content: `SERVICE AGREEMENT

This Service Agreement ("Agreement") is effective as of [EFFECTIVE_DATE]

BETWEEN:
Service Provider: [PROVIDER_NAME]
Address: [PROVIDER_ADDRESS]

AND:
Client: [CLIENT_NAME]
Address: [CLIENT_ADDRESS]

1. SERVICES
The Service Provider agrees to provide the following services:
[DETAILED_SCOPE_OF_WORK]

2. DELIVERABLES
- [DELIVERABLE_1]
- [DELIVERABLE_2]
- [DELIVERABLE_3]

3. TIMELINE
Project Start Date: [START_DATE]
Project End Date: [END_DATE]
Key Milestones:
- [MILESTONE_1]: [DATE_1]
- [MILESTONE_2]: [DATE_2]

4. COMPENSATION
Total Project Fee: [TOTAL_FEE]
Payment Schedule:
- [PAYMENT_1]: Due on [DATE]
- [PAYMENT_2]: Due on [DATE]

5. INTELLECTUAL PROPERTY
Upon full payment, all deliverables and work product shall become the property of the Client.

6. CONFIDENTIALITY
Both parties agree to maintain the confidentiality of any proprietary information shared during this engagement.

7. TERMINATION
Either party may terminate this Agreement with [NOTICE_PERIOD] days written notice.

8. GOVERNING LAW
This Agreement shall be governed by the laws of [JURISDICTION].

AGREED AND ACCEPTED:

SERVICE PROVIDER:                    CLIENT:
_____________________               _____________________
Name:                               Name:
Title:                              Title:
Date:                               Date:`
  },
  {
    id: 'quote-template',
    name: 'Price Quote',
    category: 'sales',
    description: 'Professional quotation template for providing pricing to potential customers.',
    icon: Receipt,
    popular: true,
    content: `PRICE QUOTATION

Quote Number: [QUOTE_NUMBER]
Quote Date: [QUOTE_DATE]
Valid Until: [EXPIRY_DATE]

FROM:
[YOUR_COMPANY_NAME]
[YOUR_ADDRESS]
[YOUR_PHONE]
[YOUR_EMAIL]

TO:
[CUSTOMER_NAME]
[CUSTOMER_COMPANY]
[CUSTOMER_ADDRESS]

Dear [CUSTOMER_NAME],

Thank you for your interest in our products/services. Please find our quotation below:

QUOTATION DETAILS:

| # | Item Description | Qty | Unit Price | Total |
|---|-----------------|-----|------------|-------|
| 1 | [ITEM_1] | [QTY_1] | [PRICE_1] | [TOTAL_1] |
| 2 | [ITEM_2] | [QTY_2] | [PRICE_2] | [TOTAL_2] |

Subtotal: [SUBTOTAL]
Discount ([DISCOUNT]%): -[DISCOUNT_AMOUNT]
Shipping: [SHIPPING]
Tax ([TAX_RATE]%): [TAX]
─────────────────────────
GRAND TOTAL: [GRAND_TOTAL]

TERMS & CONDITIONS:
• Payment: [PAYMENT_TERMS]
• Delivery: [DELIVERY_TERMS]
• Warranty: [WARRANTY_TERMS]

This quote is valid for [VALIDITY_DAYS] days from the date above.

To proceed with this order, please sign below and return a copy to us.

We look forward to doing business with you.

Best regards,
[YOUR_NAME]
[YOUR_TITLE]

─────────────────────────────
ACCEPTANCE

I accept the above quotation and agree to the terms and conditions.

Signature: _____________________
Name: _____________________
Date: _____________________`
  },
  {
    id: 'invoice-template',
    name: 'Invoice',
    i18nName: 'templates.lib.invoice',
    i18nDesc: 'templates.lib.invoiceDesc',
    category: 'finance',
    description: 'Professional invoice template for billing customers.',
    icon: Receipt,
    popular: false,
    content: `INVOICE

Invoice Number: [INVOICE_NUMBER]
Invoice Date: [INVOICE_DATE]
Due Date: [DUE_DATE]

FROM:
[YOUR_COMPANY_NAME]
[YOUR_ADDRESS]
Tax ID: [YOUR_TAX_ID]

BILL TO:
[CUSTOMER_NAME]
[CUSTOMER_COMPANY]
[CUSTOMER_ADDRESS]

INVOICE DETAILS:

| # | Description | Qty | Rate | Amount |
|---|-------------|-----|------|--------|
| 1 | [ITEM_1] | [QTY_1] | [RATE_1] | [AMOUNT_1] |

Subtotal: [SUBTOTAL]
Tax ([TAX_RATE]%): [TAX_AMOUNT]
─────────────────────────
TOTAL DUE: [TOTAL_DUE]

PAYMENT INFORMATION:
Bank: [BANK_NAME]
Account: [ACCOUNT_NUMBER]
Routing: [ROUTING_NUMBER]

Payment Terms: [PAYMENT_TERMS]

Thank you for your business!`
  },
  {
    id: 'letter-of-intent',
    name: 'Letter of Intent',
    category: 'legal',
    description: 'Letter of intent template for expressing serious interest in a business transaction.',
    icon: FileSignature,
    popular: false,
    content: `LETTER OF INTENT

Date: [DATE]

To: [RECIPIENT_NAME]
    [RECIPIENT_COMPANY]
    [RECIPIENT_ADDRESS]

Re: Letter of Intent for [TRANSACTION_TYPE]

Dear [RECIPIENT_NAME],

This Letter of Intent ("LOI") sets forth the principal terms upon which [YOUR_COMPANY] ("Buyer") proposes to [TRANSACTION_DESCRIPTION] with [TARGET_COMPANY] ("Seller").

1. TRANSACTION OVERVIEW
[DETAILED_TRANSACTION_DESCRIPTION]

2. PURCHASE PRICE
The proposed purchase price is [PURCHASE_PRICE], subject to customary adjustments.

3. DUE DILIGENCE
Buyer shall have [DUE_DILIGENCE_PERIOD] days to conduct due diligence.

4. EXCLUSIVITY
Seller agrees to negotiate exclusively with Buyer for [EXCLUSIVITY_PERIOD] days.

5. CONFIDENTIALITY
Both parties agree to maintain the confidentiality of this LOI and all negotiations.

6. NON-BINDING
This LOI is non-binding except for the confidentiality and exclusivity provisions.

7. EXPENSES
Each party shall bear its own expenses in connection with this transaction.

8. EXPIRATION
This LOI shall expire if not accepted by [EXPIRATION_DATE].

We look forward to working with you on this transaction.

Sincerely,

[YOUR_NAME]
[YOUR_TITLE]
[YOUR_COMPANY]

ACKNOWLEDGED AND AGREED:

_____________________
[RECIPIENT_NAME]
[RECIPIENT_TITLE]
Date: _____________________`
  }
];

const categoryConfig = {
  all: { label: 'All Templates', icon: FileText },
  legal: { label: 'Legal', icon: Shield },
  sales: { label: 'Sales', icon: Handshake },
  services: { label: 'Services', icon: Briefcase },
  finance: { label: 'Finance', icon: Receipt }
};

// Template Card Component
function TemplateCard({ template, onUse, onPreview, isCustom, onEdit, onDelete }) {
  const { t } = useTranslation();
  const Icon = isCustom ? FileText : template.icon || FileText;

  return (
    <Card 
      className="hover:shadow-md transition-all cursor-pointer group relative"
      data-testid={`template-card-${template.id || template._id}`}
    >
      <CardHeader className="pb-2 pr-8">
        <div className="flex items-start justify-between">
          <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
            <Icon className="h-5 w-5 text-primary" />
          </div>
          {template.popular && !isCustom && (
            <Badge variant="secondary" className="text-xs">
              <Star className="h-3 w-3 mr-1" />
              Popular
            </Badge>
          )}
        </div>
        
        {isCustom && (
          <div className="absolute top-4 right-4" onClick={(e) => e.stopPropagation()}>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-foreground">
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => onEdit(template)}>
                  <Edit className="h-4 w-4 mr-2" />
                  {t("templates.editTemplate") || "Edit Template"}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => onDelete(template)} className="text-destructive">
                  <Trash2 className="h-4 w-4 mr-2" />
                  {t("templates.delete") || "Delete"}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )}

        <CardTitle className="text-base mt-3">{template.i18nName ? (t(template.i18nName) || template.name) : template.name}</CardTitle>
        <CardDescription className="text-xs line-clamp-2">
          {(template.i18nDesc ? (t(template.i18nDesc) || template.description) : template.description) || "No description provided."}
        </CardDescription>
      </CardHeader>
      <CardFooter className="pt-2 gap-2">
        <Button 
          variant="outline" 
          size="sm" 
          className="flex-1"
          onClick={() => onPreview(template)}
        >
          <Eye className="h-3 w-3 mr-1" />
          {t("templates.preview") || "Preview"}
        </Button>
        <Button 
          size="sm" 
          className="flex-1"
          onClick={() => onUse(template)}
        >
          <Copy className="h-3 w-3 mr-1" />
          Use
        </Button>
      </CardFooter>
    </Card>
  );
}

// Template Preview Dialog
function TemplatePreviewDialog({ template, open, onOpenChange, onUse }) {
  if (!template) return null;
  const Icon = template.icon || FileText;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
              <Icon className="h-5 w-5 text-primary" />
            </div>
            <div>
              <DialogTitle>{template.i18nName ? (t(template.i18nName) || template.name) : template.name}</DialogTitle>
              <DialogDescription>{template.description}</DialogDescription>
            </div>
          </div>
        </DialogHeader>
        <div className="flex-1 overflow-auto border rounded-lg bg-muted/30 p-4">
          <pre className="text-sm whitespace-pre-wrap font-mono text-foreground">
            {template.content}
          </pre>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
          <Button onClick={() => {
            onUse(template);
            onOpenChange(false);
          }}>
            <Copy className="h-4 w-4 mr-2" />
            Use This Template
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Custom Template Form
function CreateTemplateDialog({ open, onOpenChange, initialData = null }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState({
    name: '',
    category: 'legal',
    description: '',
    content: ''
  });
  const [errors, setErrors] = useState({});

  const isEditing = !!initialData;

  React.useEffect(() => {
    if (open) {
      if (initialData) {
        setFormData({
          name: initialData.name || '',
          category: initialData.category || 'legal',
          description: initialData.description || '',
          content: initialData.content || ''
        });
      } else {
        setFormData({ name: '', category: 'legal', description: '', content: '' });
      }
      setErrors({});
    }
  }, [open, initialData]);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name?.trim()) {
      newErrors.name = 'Template Name is required';
    } else if (formData.name.length > 50) {
      newErrors.name = 'Name must be less than 50 characters';
    }

    if (!formData.content?.trim()) {
      newErrors.content = 'Template Content is required';
    } else if (formData.content.length > 10000) {
      newErrors.content = 'Content is too long (max 10,000 chars)';
    }

    if (formData.description && formData.description.length > 200) {
      newErrors.description = 'Description must be less than 200 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const createMutation = useMutation({
    mutationFn: (data) => templates.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries(['templates']);
      toast.success('Template created successfully');
      onOpenChange(false);
    },
    onError: (error) => toast.error(error.message || 'Failed to create template')
  });

  const updateMutation = useMutation({
    mutationFn: (data) => templates.update(initialData._id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['templates']);
      toast.success('Template updated successfully');
      onOpenChange(false);
    },
    onError: (error) => toast.error(error.message || 'Failed to update template')
  });

  const isSubmitting = createMutation.isPending || updateMutation.isPending;

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }
    
    // Clean up empty strings to null for optional fields
    const cleanedData = {
      ...formData,
      description: formData.description || null
    };

    if (isEditing) {
      updateMutation.mutate(cleanedData);
    } else {
      createMutation.mutate(cleanedData);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(open) => {
        onOpenChange(open);
        if(!open) setErrors({});
    }}>
      <DialogContent className="w-[95vw] max-w-2xl max-h-[90vh] flex flex-col p-0 gap-0">
        <DialogHeader className="p-6 pb-2 flex-shrink-0">
          <DialogTitle>{isEditing ? t('templates.editTemplate') || 'Edit Template' : t('templates.createCustomTemplate') || 'Create Custom Template'}</DialogTitle>
          <DialogDescription>
            {isEditing ? t('templates.editDesc') || 'Update your document template' : t('templates.createDesc') || 'Create your own reusable document template'}
          </DialogDescription>
        </DialogHeader>
        
        <div className="flex-1 overflow-y-auto px-6 py-2">
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className={errors.name ? "text-destructive" : ""}>
                  Template Name <span className="text-destructive ml-1">*</span>
                </Label>
                <Input 
                  placeholder={t("templates.ph.templateName") || "e.g., Custom NDA"}
                  value={formData.name}
                  onChange={e => {
                    setFormData({...formData, name: e.target.value});
                    if (errors.name) setErrors(prev => ({...prev, name: null}));
                  }}
                  className={errors.name ? "border-destructive" : ""}
                  maxLength={50}
                />
                {errors.name && <p className="text-xs text-destructive mt-1">{errors.name}</p>}
              </div>
              <div className="space-y-2">
                <Label>Category</Label>
                <Select 
                  value={formData.category}
                  onValueChange={v => setFormData({...formData, category: v})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="legal">Legal</SelectItem>
                    <SelectItem value="sales">Sales</SelectItem>
                    <SelectItem value="services">Services</SelectItem>
                    <SelectItem value="finance">Finance</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label className={errors.description ? "text-destructive" : ""}>
                {t("templates.description") || "Description"}
              </Label>
              <Input 
                placeholder={t("templates.ph.description") || "Brief description of the template"}
                value={formData.description}
                onChange={e => {
                  setFormData({...formData, description: e.target.value});
                  if (errors.description) setErrors(prev => ({...prev, description: null}));
                }}
                className={errors.description ? "border-destructive" : ""}
                maxLength={200}
              />
              <div className="flex justify-between text-xs text-muted-foreground mt-1">
                {errors.description ? <span className="text-destructive">{errors.description}</span> : <span></span>}
                <span>{formData.description.length}/200</span>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label className={errors.content ? "text-destructive" : ""}>
                Template Content <span className="text-destructive ml-1">*</span>
              </Label>
              <Textarea 
                placeholder={t("templates.ph.content") || "Enter your template content here..."}
                className={`min-h-[200px] font-mono text-sm ${errors.content ? "border-destructive" : ""}`}
                value={formData.content}
                onChange={e => {
                  setFormData({...formData, content: e.target.value});
                  if (errors.content) setErrors(prev => ({...prev, content: null}));
                }}
              />
              {errors.content && <p className="text-xs text-destructive mt-1">{errors.content}</p>}
              {!errors.content && (
                <p className="text-xs text-muted-foreground mt-1">
                  Use [PLACEHOLDER] format for variable fields that will be filled in later
                </p>
              )}
            </div>
          </div>
        </div>
        
        <div className="p-6 pt-2 flex-shrink-0 border-t bg-background">
          <DialogFooter className="pt-2 gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              {t("common.cancel") || "Cancel"}
            </Button>
            <Button onClick={handleSubmit} disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {isEditing ? t('templates.saving') || 'Saving...' : t('templates.creating') || 'Creating...'}
                </>
              ) : (
                <>
                  {isEditing ? <Edit className="h-4 w-4 mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
                  {isEditing ? t('templates.saveChanges') || 'Save Changes' : t('templates.createTemplate') || 'Create Template'}
                </>
              )}
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function TemplatesPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [previewTemplate, setPreviewTemplate] = useState(null);
  
  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateToDelete, setTemplateToDelete] = useState(null);

  // Fetch custom templates
  const { data: customTemplatesRaw = [], isLoading: isLoadingCustom } = useQuery({
    queryKey: ['templates'],
    queryFn: templates.list,
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id) => templates.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries(['templates']);
      toast.success('Template deleted successfully');
      setTemplateToDelete(null);
    },
    onError: (error) => toast.error(error.message || 'Failed to delete template')
  });

  // Filter default templates
  const filteredDefaultTemplates = TEMPLATE_LIBRARY.filter(template => {
    const matchesSearch = template.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         template.description?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = categoryFilter === 'all' || template.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  // Filter custom templates
  const filteredCustomTemplates = customTemplatesRaw.filter(template => {
    const matchesSearch = template.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         template.description?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = categoryFilter === 'all' || template.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  const handleUseTemplate = (template) => {
    // Copy to clipboard
    navigator.clipboard.writeText(template.content);
    toast.success(`${template.i18nName ? (t(template.i18nName) || template.name) : template.name} copied to clipboard`);
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="templates-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Template Library</h1>
          <p className="text-muted-foreground">Ready-to-use document templates for your business</p>
        </div>
        <Button onClick={() => setCreateDialogOpen(true)} data-testid="create-template-btn">
          <Plus className="h-4 w-4 mr-2" />
          {t("templates.createTemplate") || "Create Template"}
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder={t("templates.searchPlaceholder") || "Search templates..."}
            className="pl-9"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            data-testid="template-search"
          />
        </div>
        <Tabs value={categoryFilter} onValueChange={setCategoryFilter}>
          <TabsList>
            {Object.entries(categoryConfig).map(([key, config]) => (
              <TabsTrigger key={key} value={key} className="gap-1">
                <config.icon className="h-4 w-4" />
                <span className="hidden sm:inline">{config.label}</span>
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
      </div>

      {/* Popular Templates (From Defaults) */}
      {categoryFilter === 'all' && !searchQuery && (
        <div className="space-y-3">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Star className="h-5 w-5 text-amber-500" />
            {t("templates.popularTemplates") || "Popular Templates"}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {TEMPLATE_LIBRARY.filter(t => t.popular).map(template => (
              <TemplateCard 
                key={template.id}
                template={template}
                onUse={handleUseTemplate}
                onPreview={setPreviewTemplate}
                isCustom={false}
              />
            ))}
          </div>
        </div>
      )}

      {/* Default Templates */}
      <div className="space-y-3">
        <h2 className="text-lg font-semibold">
          {categoryFilter === 'all' ? t('templates.defaultTemplates') || 'Default Templates' : categoryConfig[categoryFilter].label}
          <span className="text-muted-foreground font-normal ml-2">
            ({filteredDefaultTemplates.length})
          </span>
        </h2>
        
        {filteredDefaultTemplates.length === 0 ? (
          <div className="text-muted-foreground text-sm py-4">No default templates match your search.</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {filteredDefaultTemplates.map(template => (
              <TemplateCard 
                key={template.id}
                template={template}
                onUse={handleUseTemplate}
                onPreview={setPreviewTemplate}
                isCustom={false}
              />
            ))}
          </div>
        )}
      </div>

      {/* Custom Templates */}
      <div className="space-y-3 pt-6 border-t">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">
            Custom Templates
            <span className="text-muted-foreground font-normal ml-2">
              ({filteredCustomTemplates.length})
            </span>
          </h2>
        </div>
        
        {isLoadingCustom ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {[1, 2, 3].map(i => (
              <Card key={i} className="h-40">
                <CardHeader>
                  <Skeleton className="h-10 w-10 rounded-lg mb-2" />
                  <Skeleton className="h-5 w-3/4 mb-1" />
                  <Skeleton className="h-4 w-full" />
                </CardHeader>
              </Card>
            ))}
          </div>
        ) : filteredCustomTemplates.length === 0 ? (
          <Card className="bg-muted/30 border-dashed">
            <CardContent className="flex flex-col items-center justify-center py-12">
              <FileText className="h-12 w-12 text-muted-foreground/50 mb-4" />
              <p className="text-muted-foreground mb-4">No custom templates found</p>
              <Button onClick={() => setCreateDialogOpen(true)} variant="outline">
                Create your first template
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {filteredCustomTemplates.map(template => (
              <TemplateCard 
                key={template._id}
                template={template}
                onUse={handleUseTemplate}
                onPreview={setPreviewTemplate}
                isCustom={true}
                onEdit={() => {
                  setEditingTemplate(template);
                  setCreateDialogOpen(true);
                }}
                onDelete={(t) => setTemplateToDelete(t)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Preview Dialog */}
      <TemplatePreviewDialog
        template={previewTemplate}
        open={!!previewTemplate}
        onOpenChange={(open) => !open && setPreviewTemplate(null)}
        onUse={handleUseTemplate}
      />

      {/* Create / Edit Dialog */}
      <CreateTemplateDialog
        open={createDialogOpen}
        onOpenChange={(open) => {
          setCreateDialogOpen(open);
          if (!open) {
            // Slight delay to allow fade out animation before resetting data
            setTimeout(() => setEditingTemplate(null), 200);
          }
        }}
        initialData={editingTemplate}
      />

      {/* Delete Confirmation Alert */}
      <AlertDialog open={!!templateToDelete} onOpenChange={(open) => !open && setTemplateToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("templates.deleteTemplate") || "Delete Template"}</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete the custom template "{templateToDelete?.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteMutation.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={(e) => {
                e.preventDefault();
                if (templateToDelete) {
                  deleteMutation.mutate(templateToDelete._id);
                }
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? "Deleting..." : "Delete Template"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
